// Event type (consultation service) schemas (architecture.md §8.3 event_types).
import { z } from 'zod';
import { slug, moneyMinor, currency } from './common.js';
import { EVENT_LOCATION, EVENT_STATUS } from '../enums.js';

// Empty string from the form means "no redirect" → null.
const emptyToNull = (v) => (typeof v === 'string' && v.trim() === '' ? null : v);

// Optional post-booking redirect target. Must be an absolute http(s) URL — anything else
// (e.g. javascript:) is rejected so it can never become an XSS sink at window.location.
const redirectUrl = z.preprocess(
  emptyToNull,
  z
    .string()
    .trim()
    .max(2000)
    .url('Enter a valid URL, e.g. https://yoursite.com/thank-you')
    .refine((v) => /^https?:\/\//i.test(v), 'URL must start with http:// or https://')
    .nullable()
    .optional(),
);

const base = z.object({
  title: z.string().trim().min(1).max(160),
  slug,
  duration_min: z.number().int().min(5).max(1440).default(30),
  buffer_before_min: z.number().int().min(0).max(1440).default(0),
  buffer_after_min: z.number().int().min(0).max(1440).default(0),
  advance_booking_days: z.number().int().min(0).max(365).default(30),
  location_type: z.enum(EVENT_LOCATION).default('online'),
  description: z.string().trim().max(2000).optional().nullable(),
  require_payment: z.boolean().default(false),
  price_minor: moneyMinor.optional().nullable(),
  currency: currency.default('INR'),
  status: z.enum(EVENT_STATUS).default('active'),
  redirect_url: redirectUrl,
});

// Mirrors the DB check: require_payment => price_minor + currency present.
const paidRule = (obj, ctx) => {
  if (obj.require_payment && (obj.price_minor == null || !obj.currency)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['price_minor'],
      message: 'price_minor and currency are required when require_payment is true',
    });
  }
};

export const createEventTypeSchema = base.superRefine(paidRule);
export const updateEventTypeSchema = base.partial().superRefine((obj, ctx) => {
  if (obj.require_payment === true && (obj.price_minor == null || !obj.currency)) {
    paidRule(obj, ctx);
  }
});
